import React, { useMemo } from 'react'
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, SearchBar, Icon } from '@components'
import { useLanguage } from '@context'

const EDGE_TYPE_DEFS = [
  { key: 'contact', labelKey: 'graphScreen.edgeContact', color: '#2E78B8', icon: 'people' },
  { key: 'category', labelKey: 'graphScreen.edgeCategory', color: '#CF874B', icon: 'category' },
  { key: 'hashtag', labelKey: 'graphScreen.edgeHashtag', color: '#9C27B0', icon: 'htag' },
  { key: 'profession', labelKey: 'graphScreen.edgeProfession', color: '#E91E63', icon: 'work' },
  { key: 'nationality', labelKey: 'graphScreen.edgeNationality', color: '#D39E50', icon: 'globe' },
  { key: 'city', labelKey: 'graphScreen.edgeCity', color: '#00ACC1', icon: 'mapPin' },
] as const

interface GraphControlsProps {
  mode: 'contacts-only' | 'full'
  onModeChange: (mode: 'contacts-only' | 'full') => void
  edgeTypes: string[]
  onEdgeTypesChange: (types: string[]) => void
  searchText: string
  onSearchChange: (text: string) => void
  nodeCount: number
  edgeCount: number
}

export const GraphControls: React.FC<GraphControlsProps> = ({
  mode,
  onModeChange,
  edgeTypes,
  onEdgeTypesChange,
  searchText,
  onSearchChange,
  nodeCount,
  edgeCount,
}) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const { t } = useLanguage()

  const toggleEdgeType = (type: string) => {
    if (edgeTypes.includes(type)) {
      onEdgeTypesChange(edgeTypes.filter((t) => t !== type))
    } else {
      onEdgeTypesChange([...edgeTypes, type])
    }
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <SearchBar
        value={searchText}
        onChangeText={onSearchChange}
        placeholder={t('graphScreen.searchPlaceholder')}
      />

      <View style={styles.section}>
        <Text type="bodySmallBold" style={styles.sectionTitle}>{t('graphScreen.mode')}</Text>
        <View style={styles.modeButtons}>
          <TouchableOpacity
            style={[styles.modeButton, mode === 'contacts-only' && styles.modeButtonActive]}
            onPress={() => onModeChange('contacts-only')}
          >
            <Text type="bodySmall" style={mode === 'contacts-only' ? styles.modeTextActive : styles.modeText}>
              {t('graphScreen.modeContacts')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.modeButton, mode === 'full' && styles.modeButtonActive]}
            onPress={() => onModeChange('full')}
          >
            <Text type="bodySmall" style={mode === 'full' ? styles.modeTextActive : styles.modeText}>
              {t('graphScreen.modeFull')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.section}>
        <Text type="bodySmallBold" style={styles.sectionTitle}>{t('graphScreen.relations')}</Text>
        {EDGE_TYPE_DEFS.map((opt) => {
          // « Contacts » mode only draws contact links: the other rows are dimmed.
          const isDisabled = mode === 'contacts-only' && opt.key !== 'contact'
          const isActive = edgeTypes.includes(opt.key) && !isDisabled
          return (
            <TouchableOpacity
              key={opt.key}
              style={[
                styles.edgeTypeRow,
                isActive && { backgroundColor: opt.color + '15' },
                isDisabled && styles.edgeTypeRowDisabled,
              ]}
              disabled={isDisabled}
              onPress={() => toggleEdgeType(opt.key)}
            >
              <View style={[styles.colorDot, { backgroundColor: opt.color }]} />
              <Icon name={opt.icon as any} size={14} color={isActive ? opt.color : '#999'} />
              <Text type="bodySmall" style={[styles.edgeTypeLabel, isActive ? { color: opt.color as string } : {}]}>
                {t(opt.labelKey)}
              </Text>
              <View style={[styles.checkbox, isActive && { backgroundColor: opt.color, borderColor: opt.color }]}>
                {isActive && <Icon name="check" size={10} color="white" />}
              </View>
            </TouchableOpacity>
          )
        })}
      </View>

      <View style={styles.statsSection}>
        <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
          {t('graphScreen.statsLine', { nodeCount, edgeCount })}
        </Text>
      </View>
    </ScrollView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      padding: 12,
    },
    section: {
      marginTop: 16,
    },
    sectionTitle: {
      color: theme.colors.secondary,
      marginBottom: 8,
      textTransform: 'uppercase',
      fontSize: 11,
      letterSpacing: 0.5,
    },
    modeButtons: {
      flexDirection: 'row',
      gap: 8,
    },
    modeButton: {
      flex: 1,
      paddingVertical: 8,
      borderRadius: 8,
      backgroundColor: '#F2F2F7',
      alignItems: 'center',
    },
    modeButtonActive: {
      backgroundColor: theme.colors.primary,
    },
    modeText: {
      color: '#666',
    },
    modeTextActive: {
      color: 'white',
      fontWeight: '600',
    },
    edgeTypeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 8,
      marginBottom: 4,
      gap: 8,
    },
    edgeTypeRowDisabled: {
      opacity: 0.4,
    },
    colorDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
    },
    edgeTypeLabel: {
      flex: 1,
      color: '#666',
    },
    checkbox: {
      width: 18,
      height: 18,
      borderRadius: 4,
      borderWidth: 1.5,
      borderColor: '#ccc',
      justifyContent: 'center',
      alignItems: 'center',
    },
    statsSection: {
      marginTop: 16,
      paddingTop: 12,
      borderTopWidth: 0.5,
      borderTopColor: '#E5E5EA',
      alignItems: 'center',
    },
  })
